import makeStyles from '@mui/styles/makeStyles'
import { variant } from '../action-buttons/action-button/style'

export const useStyles = makeStyles(({ spacing, palette, typography, breakpoints }) => ({
  root: ({ variant: variantProp }) => ({
    backgroundColor: variantProp === 'solid'
      ? variant('primary', palette).backgroundColor
      : palette.primary.shade?.[5] || palette.grey[0],
    borderBottom: variantProp === 'solid'
      ? 'none'
      : `1px solid ${palette.grey[200]}`,
    top: 64, // Position below navbar (navbar height is typically 64px)
    zIndex: 1100, // Below navbar (1200) but above content
    [breakpoints.down('md')]: {
      top: 56 // Mobile navbar is typically 56px
    }
  }),
  toolbar: {
    minHeight: 64,
    padding: spacing(0, 3),
    [breakpoints.down('md')]: {
      minHeight: 56,
      padding: spacing(0, 2)
    }
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    maxWidth: 1440,
    margin: '0 auto',
    gap: spacing(4),
    [breakpoints.down('md')]: {
      gap: spacing(2)
    }
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(2),
    flex: 1,
    minWidth: 0,
    overflow: 'hidden'
  },
  logo: {
    backgroundColor: palette.common.white,
    borderRadius: '50%',
    padding: spacing(1),
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
    '& svg': {
      fontSize: 18,
      color: variant('primary', palette).backgroundColor
    }
  },
  programName: ({ variant: variantProp }) => ({
    ...typography.body.m,
    fontWeight: typography.weight.bold,
    color: variantProp === 'solid' ? palette.common.white : palette.text.primary,
    letterSpacing: '0.5px',
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
    [breakpoints.down('md')]: {
      fontSize: '0.75rem'
    }
  }),
  programNameLink: ({ variant: variantProp }) => ({
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'none',
      '& $programName': {
        color: variantProp === 'solid' ? 'rgba(255, 255, 255, 0.85)' : variant('primary', palette).backgroundColor
      }
    }
  }),
  centerSection: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    minWidth: 0 // Allow flex shrink
  },
  breadcrumbs: {
    [breakpoints.down('sm')]: {
      display: 'none' // Hide breadcrumbs on mobile
    },
    '& .MuiBreadcrumbs-ol': {
      flexWrap: 'nowrap'
    }
  },
  breadcrumbLink: ({ variant: variantProp }) => ({
    ...typography.body.s,
    color: variantProp === 'solid' ? 'rgba(255, 255, 255, 0.7)' : palette.text.secondary,
    textDecoration: 'none',
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 200,
    '&:hover': {
      color: variantProp === 'solid' ? palette.common.white : variant('primary', palette).backgroundColor,
      textDecoration: 'underline'
    }
  }),
  breadcrumbText: ({ variant: variantProp }) => ({
    ...typography.body.s,
    color: variantProp === 'solid' ? palette.common.white : palette.text.primary,
    fontSize: '0.875rem',
    fontWeight: typography.weight.medium,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 200
  }),
  breadcrumbSeparator: ({ variant: variantProp }) => ({
    fontSize: 16,
    color: variantProp === 'solid' ? 'rgba(255, 255, 255, 0.5)' : palette.text.secondary
  }),
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(2),
    flexShrink: 0,
    [breakpoints.down('md')]: {
      gap: spacing(1)
    }
  },
  nextStepContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1),
    [breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  nextStepLabel: ({ variant: variantProp }) => ({
    ...typography.body.xs,
    color: variantProp === 'solid' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.64)', // Darkened for WCAG 2 AA contrast
    fontSize: '0.75rem',
    fontWeight: typography.weight.medium,
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  }),
  nextStepChip: ({ variant: variantProp }) => ({
    backgroundColor: variantProp === 'solid' ? 'rgba(0, 0, 0, 0.25)' : palette.grey[100], // Darkened from transparent for WCAG 2 AA contrast with white text
    color: variantProp === 'solid' ? palette.common.white : palette.text.primary,
    fontWeight: typography.weight.medium,
    fontSize: '0.875rem',
    height: 28,
    borderRadius: spacing(1.5),
    transition: 'all 0.2s ease',
    border: variantProp === 'solid' ? '1px solid rgba(255, 255, 255, 0.3)' : `1px solid ${palette.grey[300]}`,
    '&:hover': {
      backgroundColor: variantProp === 'solid' ? 'rgba(255, 255, 255, 0.3)' : palette.grey[200],
      borderColor: variantProp === 'solid' ? 'rgba(255, 255, 255, 0.5)' : palette.grey[400]
    },
    '& .MuiChip-label': {
      padding: spacing(0, 1.5),
      paddingRight: spacing(0.5)
    },
    '& .MuiChip-deleteIcon': {
      color: variantProp === 'solid' ? 'rgba(255, 255, 255, 0.7)' : palette.text.secondary,
      fontSize: 16,
      margin: spacing(0, 0.5, 0, 0),
      '&:hover': {
        color: variantProp === 'solid' ? palette.common.white : palette.primary.main
      }
    }
  }),
  nextStepIcon: {
    fontSize: 16
  },
  separator: ({ variant: variantProp }) => ({
    width: 1,
    height: 24,
    backgroundColor: variantProp === 'solid' ? 'rgba(255, 255, 255, 0.3)' : palette.grey[300]
  }),
  // Journey Steps
  journeyStepsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1.5),
    [breakpoints.down('md')]: {
      gap: spacing(1)
    }
  },
  journeyStep: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(0.75),
    flexShrink: 0
  },
  journeyStepCircle: ({ variant: variantProp }) => ({
    width: 28,
    height: 28,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    border: variantProp === 'solid' ? '2px solid rgba(255, 255, 255, 0.4)' : `2px solid ${palette.grey[300]}`,
    transition: 'all 0.2s ease'
  }),
  journeyStepComplete: ({ variant: variantProp }) => ({
    backgroundColor: variantProp === 'solid' ? 'rgba(255, 255, 255, 0.3)' : palette.grey[700],
    borderColor: variantProp === 'solid' ? 'rgba(255, 255, 255, 0.5)' : palette.grey[700]
  }),
  journeyStepCurrent: ({ variant: variantProp }) => ({
    backgroundColor: variantProp === 'solid' ? palette.common.white : palette.primary.main,
    borderColor: variantProp === 'solid' ? palette.common.white : palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }),
  focusIcon: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  journeyStepUpcoming: ({ variant: variantProp }) => ({
    backgroundColor: 'transparent',
    borderColor: variantProp === 'solid' ? 'rgba(255, 255, 255, 0.3)' : palette.grey[300]
  }),
  journeyStepIcon: ({ variant: variantProp }) => ({
    fontSize: 16,
    color: variantProp === 'solid' ? palette.common.white : palette.common.white
  }),
  journeyStepNumber: ({ variant: variantProp }) => ({
    ...typography.body.xs,
    fontWeight: typography.weight.bold,
    fontSize: '0.75rem',
    lineHeight: 1
  }),
  journeyStepLabel: ({ variant: variantProp }) => ({
    ...typography.body.xs,
    color: variantProp === 'solid' ? 'rgba(255, 255, 255, 0.9)' : palette.text.primary,
    fontSize: '0.8125rem',
    fontWeight: typography.weight.medium,
    whiteSpace: 'nowrap',
    [breakpoints.down('md')]: {
      display: 'none'
    }
  }),
  journeyStepConnector: ({ variant: variantProp }) => ({
    width: 24,
    height: 2,
    backgroundColor: variantProp === 'solid' ? 'rgba(255, 255, 255, 0.3)' : palette.grey[300],
    flexShrink: 0,
    [breakpoints.down('md')]: {
      width: 16
    }
  }),
  progressLabel: ({ variant: variantProp }) => ({
    ...typography.body.s,
    color: variantProp === 'solid' ? 'rgba(255, 255, 255, 0.7)' : palette.text.secondary,
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
    [breakpoints.down('sm')]: {
      display: 'none'
    }
  }),
  progressContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1),
    minWidth: 120,
    [breakpoints.down('md')]: {
      minWidth: 80
    }
  },
  progressBar: ({ variant: variantProp }) => ({
    flex: 1,
    height: 6,
    borderRadius: 3,
    backgroundColor: variantProp === 'solid' ? 'rgba(255, 255, 255, 0.3)' : palette.grey[200],
    '& .MuiLinearProgress-bar': {
      backgroundColor: variantProp === 'solid' ? palette.common.white : variant('primary', palette).backgroundColor,
      borderRadius: 3
    }
  }),
  progressValue: ({ variant: variantProp }) => ({
    ...typography.body.s,
    fontWeight: typography.weight.bold,
    color: variantProp === 'solid' ? palette.common.white : palette.text.primary,
    fontSize: '0.875rem',
    minWidth: 35,
    textAlign: 'right'
  })
}))
